import { useCallback, useRef, useState } from 'react';
import { Loader, Modal } from '@beholos/holoscommon';
import CardFields, { type CardFieldsHandle } from './CardFields';
import { updatePaymentMethod } from '../../services/payment.service';
import { CircleCheck } from 'lucide-react';

interface UpdatePaymentMethodModalProps {
  isOpen: boolean;
  closeModal: () => void;
  refetch: () => Promise<void>;
  chargeAmount: number;
}

function UpdatePaymentMethodModalInner({ isOpen, closeModal, refetch, chargeAmount }: UpdatePaymentMethodModalProps) {
  const cardFieldsRef = useRef<CardFieldsHandle>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isBrickReady, setIsBrickReady] = useState(false);
  const handleBrickReady = useCallback(() => setIsBrickReady(true), []);

  const handleClose = () => {
    if (loading) return;
    setError(null);
    setSuccess(false);
    setIsBrickReady(false);
    closeModal();
  };

  // Called by the Brick's onSubmit after successful tokenization
  const handleBrickSubmit = useCallback(async (formData: any) => {
    if (!formData?.token) {
      setError('No se pudo crear el método de pago.');
      setLoading(false);
      return;
    }

    const result = await updatePaymentMethod({ paymentMethodId: formData.token });

    if (!result) {
      setError('No se pudo actualizar el método de pago. Inténtalo de nuevo.');
      setLoading(false);
      return;
    }

    await refetch();
    setLoading(false);
    setSuccess(true);
  }, [refetch]);

  const handleSubmit = () => {
    setError(null);
    setLoading(true);

    if (!cardFieldsRef.current) {
      setError('No se pudo inicializar el formulario de pago.');
      setLoading(false);
      return;
    }

    cardFieldsRef.current.triggerSubmit();
  };

  return (
    <Modal
      containerStyles="bg-white rounded-2xl shadow-lg max-w-md w-full mx-4 relative"
      isOpen={isOpen}
      onClose={handleClose}
    >
      {success ? (
        <div className="p-8 flex flex-col items-center text-center gap-4">
          <CircleCheck size={48} className="text-green-500" />
          <h3 className="text-xl font-bold font-juana">¡Método de pago actualizado!</h3>
          <p className="text-gray-600 text-sm">
            Tu método de pago fue actualizado exitosamente. Nuestro equipo procesará el pago pendiente
            a la brevedad.
          </p>
          <button
            className="mt-2 bg-black text-white px-8 py-2 rounded-full hover:bg-gray-800 transition-colors"
            onClick={handleClose}
          >
            Cerrar
          </button>
        </div>
      ) : (
        <>
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-2xl font-medium font-juana">Actualizar método de pago</h3>
            <p className="text-sm text-gray-500 mt-1">
              Ingresa un nuevo método de pago para reactivar tu suscripción.
            </p>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Se realizará un cobro por el total de tu plan: S/ {chargeAmount.toFixed(2)}.
                Este pago es necesario para reactivar tu suscripción.
              </div>

              <CardFields
                transactionAmount={chargeAmount}
                onReady={handleBrickReady}
                onBrickSubmit={handleBrickSubmit}
                ref={cardFieldsRef}
              />

              {error && (
                <p className="text-red-600 text-sm">{error}</p>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-6 py-2 border border-black rounded-full text-black hover:bg-gray-100 transition-colors text-sm disabled:opacity-50 cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !isBrickReady}
              className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader />}
              Guardar método de pago
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export function UpdatePaymentMethodModal(props: UpdatePaymentMethodModalProps) {
  return <UpdatePaymentMethodModalInner {...props} />;
}
